package hk.com.vr360_demo2.webapp.component.editor_rewrite.component

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaLinkHotspot
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaSceneConfig
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useState
import web.cssom.AlignItems
import web.cssom.Auto
import web.cssom.Background
import web.cssom.Border
import web.cssom.BoxSizing
import web.cssom.Color
import web.cssom.Cursor
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.FontWeight
import web.cssom.JustifyContent
import web.cssom.LineStyle
import web.cssom.ObjectFit
import web.cssom.Overflow
import web.cssom.Padding
import web.cssom.Position
import web.cssom.TextAlign
import web.cssom.TextOverflow
import web.cssom.WhiteSpace
import web.cssom.number
import web.cssom.pct
import web.cssom.px
import web.html.Loading
import web.html.HTMLElement
import kotlin.js.unsafeCast

external interface EditorHotspotListPanelProps : Props {
    var currentSceneId: String?
    var scenes: List<PanoramaSceneConfig>
    var hotspots: List<PanoramaLinkHotspot>
    var onCreateHotspot: (() -> Unit)?
    var onFocusHotspot: ((PanoramaLinkHotspot) -> Unit)?
    var onEditHotspot: ((PanoramaLinkHotspot) -> Unit)?
    var onDeleteHotspot: ((PanoramaLinkHotspot) -> Unit)?
    var onMoveHotspot: ((PanoramaLinkHotspot) -> Unit)?
}

val EditorHotspotListPanel = FC<EditorHotspotListPanelProps> { props ->
    val hotspotItems = props.hotspots
    val (brokenTargetPreviews, setBrokenTargetPreviews) = useState<Map<String, Boolean>>(emptyMap())
    val (isCurrentPreviewBroken, setIsCurrentPreviewBroken) = useState(false)

    useEffect(props.currentSceneId) {
        setIsCurrentPreviewBroken(false)
    }

    fun getTargetLabel(targetId: String?): String {
        if (targetId.isNullOrEmpty()) return "未設置目標"
        val scene = props.scenes.find { it.id == targetId }
        return scene?.name ?: targetId
    }

    fun getTargetPreviewUrl(targetId: String?): String? {
        if (targetId.isNullOrEmpty()) return null
        val scene = props.scenes.find { it.id == targetId }
        return scene?.cover
    }

    fun getCurrentPreviewUrl(): String? {
        val sceneId = props.currentSceneId ?: return null
        return "/tiles/${sceneId}/preview.jpg"
    }

    div {
        css {
            right = 200.px
            position = Position.absolute
            top = 120.px
            zIndex = 31.asDynamic()
            width = 250.px
            background = "rgba(0, 0, 0, 0.36)".unsafeCast<Background>()
            borderRadius = 2.px
            padding = 10.px
            boxSizing = BoxSizing.borderBox
            display = Display.flex
            flexDirection = FlexDirection.column
            asDynamic().pointerEvents = "auto"
        }

        div {
            css {
                display = Display.flex
                alignItems = AlignItems.center
                justifyContent = JustifyContent.spaceBetween
                gap = 8.px
                marginBottom = 8.px
            }
            div {
                css {
                    fontSize = 13.px
                    fontWeight = FontWeight.bold
                    color = Color("#fff")
                }
                +"已添加 "
                span {
                    css { color = Color("#f26522") }
                    +"${hotspotItems.size}"
                }
                +" 個全景切換熱點"
            }
        }

        button {
            css {
                border = 0.px
                background = "#f26522".unsafeCast<Background>()
                color = Color("#fff")
                padding = Padding(6.px, 8.px)
                marginBottom = 8.px
                borderRadius = 2.px
                fontSize = 12.px
                cursor = Cursor.pointer
                display = Display.flex
                alignItems = AlignItems.center
                justifyContent = JustifyContent.center
                flexDirection = FlexDirection.row
                position = Position.relative
                asDynamic().transition = "background 0.2s"
                hover {
                    background = "rgb(255, 131, 77)".unsafeCast<Background>()
                }
            }
            onClick = { props.onCreateHotspot?.invoke() }
            img {
                css {
                    width = 16.px
                    height = 16.px
                    position = Position.absolute
                    left = 8.px
                    asDynamic().filter = "brightness(0) saturate(100%) invert(100%)"
                }
                src = "/assets/images/plus.svg"
                alt = "添加熱點"
            }
            +"添加熱點"
        }

        div {
            css {
                maxHeight = 280.px
                overflowY = Auto.auto
                display = Display.flex
                flexDirection = FlexDirection.column
                gap = 6.px
            }

            if (hotspotItems.isEmpty()) {
                div {
                    css {
                        color = "rgba(255, 255, 255, 0.72)".unsafeCast<Color>()
                        fontSize = 12.px
                        padding = Padding(6.px, 0.px)
                    }
                    +"暫無熱點"
                }
            } else {
                hotspotItems.forEachIndexed { index, hotspot ->
                    val targetId = hotspot.target
                    val previewUrl = getTargetPreviewUrl(targetId)
                    val currentPreviewUrl = getCurrentPreviewUrl()
                    val isTargetBroken = targetId.isNotEmpty() && (brokenTargetPreviews[targetId] == true)
                    val coverUrl = if (!isTargetBroken && previewUrl != null) previewUrl else currentPreviewUrl
                    val isPreviewBroken = coverUrl.isNullOrEmpty() || isCurrentPreviewBroken

                    div {
                        key = (hotspot.hotspotId ?: "${hotspot.target}-${index}").unsafeCast<Key>()
                        css {
                            display = Display.flex
                            alignItems = AlignItems.center
                            gap = 8.px
                            width = 100.pct
                            height = 40.px
                            textAlign = TextAlign.left
                            cursor = Cursor.pointer
                            background = "rgba(0, 0, 0, 0.14)".unsafeCast<Background>()
                            color = Color("#fff")
                            fontSize = 12.px
                            padding = Padding(
                                left = 0.px, right = 8.px,
                                top = 0.px,
                                bottom = 0.px
                            )
                            hover {
                                background = "rgba(255, 255, 255, 0.12)".unsafeCast<Background>()
                            }
                        }
                        onClick = { props.onFocusHotspot?.invoke(hotspot) }

                        div {
                            css {
                                width = 40.px
                                height = 40.px
                                backgroundColor = Color("#f26522")
                                overflow = Overflow.hidden
                                alignItems = AlignItems.center
                                justifyContent = JustifyContent.center
                                flexShrink = number(0.0)
                            }
                            if (isPreviewBroken) {
                                span {
                                    css {
                                        width = 10.px
                                        height = 10.px
                                        borderRadius = 50.pct
                                        border = Border(2.px, LineStyle.solid, "rgba(253, 228, 40, 0.95)".unsafeCast<Color>())
                                        background = "rgba(253, 228, 40, 0.25)".unsafeCast<Background>()
                                        boxSizing = BoxSizing.borderBox
                                        flexShrink = number(0.0)
                                    }
                                }
                            } else {
                                img {
                                    css {
                                        width = 100.pct
                                        height = 100.pct
                                        objectFit = ObjectFit.cover
                                        display = Display.block
                                    }
                                    src = coverUrl ?: ""
                                    alt = ""
                                    loading = "lazy".unsafeCast<Loading>()
                                    onError = {
                                        if (coverUrl == previewUrl && targetId.isNotEmpty()) {
                                            setBrokenTargetPreviews { prev ->
                                                prev + (targetId to true)
                                            }
                                        } else {
                                            setIsCurrentPreviewBroken(true)
                                        }
                                    }
                                }
                            }
                        }

                        span {
                            css {
                                color = "rgba(255, 255, 255, 0.72)".unsafeCast<Color>()
                                minWidth = 12.px
                            }
                            +"#${index + 1}"
                        }

                        span {
                            css {
                                whiteSpace = WhiteSpace.nowrap
                                overflow = Overflow.hidden
                                textOverflow = TextOverflow.ellipsis
                                flexGrow = number(1.0)
                            }
                            +getTargetLabel(hotspot.target)
                        }

                        button {
                            css {
                                border = 0.px
                                background = Color.transparent
                                color = Color("#ffffff")
                                padding = Padding(
                                    right = 4.px,
                                    top = 0.px,
                                    bottom = 0.px,
                                    left = 0.px
                                )
                                cursor = Cursor.pointer
                                display = Display.flex
                                alignItems = AlignItems.center
                                justifyContent = JustifyContent.center
                            }
                            onClick = { event ->
                                event.stopPropagation()
                                props.onEditHotspot?.invoke(hotspot)
                            }
                            img {
                                css {
                                    width = 16.px
                                    height = 16.px
                                    display = Display.block
                                    asDynamic().filter = "brightness(0) saturate(100%) invert(100%)"
                                    asDynamic().transition = "filter 0.2s"
                                    hover {
                                        asDynamic().filter =
                                            "brightness(0) saturate(100%) invert(54%) sepia(64%) saturate(1939%) hue-rotate(346deg) brightness(99%) contrast(91%)"
                                    }
                                }
                                src = "/assets/images/edit.svg"
                                alt = "編輯"
                            }
                        }

                        button {
                            css {
                                border = 0.px
                                background = Color.transparent
                                color = Color("#ffffff")
                                padding = Padding(0.px, 0.px)
                                cursor = Cursor.pointer
                                display = Display.flex
                                alignItems = AlignItems.center
                                justifyContent = JustifyContent.center
                            }
                            onClick = { event ->
                                event.stopPropagation()
                                props.onDeleteHotspot?.invoke(hotspot)
                            }
                            img {
                                css {
                                    width = 16.px
                                    height = 16.px
                                    display = Display.block
                                    asDynamic().filter = "brightness(0) saturate(100%) invert(100%)"
                                    asDynamic().transition = "filter 0.2s"
                                    hover {
                                        asDynamic().filter =
                                            "brightness(0) saturate(100%) invert(54%) sepia(64%) saturate(1939%) hue-rotate(346deg) brightness(99%) contrast(91%)"
                                    }
                                }
                                src = "/assets/images/delete.svg"
                                alt = "刪除"
                            }
                        }

                        button {
                            css {
                                border = 0.px
                                background = Color.transparent
                                color = Color("#ffffff")
                                padding = Padding(0.px, 0.px)
                                cursor = Cursor.pointer
                                display = Display.flex
                                alignItems = AlignItems.center
                                justifyContent = JustifyContent.center
                            }
                            onClick = { event ->
                                event.stopPropagation()
                                props.onMoveHotspot?.invoke(hotspot)
                            }
                            img {
                                css {
                                    width = 16.px
                                    height = 16.px
                                    display = Display.block
                                    asDynamic().filter = "brightness(0) saturate(100%) invert(100%)"
                                    asDynamic().transition = "filter 0.2s"
                                    hover {
                                        asDynamic().filter =
                                            "brightness(0) saturate(100%) invert(54%) sepia(64%) saturate(1939%) hue-rotate(346deg) brightness(99%) contrast(91%)"
                                    }
                                }
                                src = "/assets/images/move.svg"
                                alt = "移動"
                            }
                        }
                    }
                }
            }
        }
    }
}
